import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { takedownApi } from "core/api";
import { ChannelEffectiveness } from "core/types/takedown.types";

const CHANNEL_LABEL_KEY: Record<string, string> = {
  email_diffida: "takedown.effectiveness.channel.email_diffida",
  pec: "takedown.effectiveness.channel.pec",
  cloudflare: "takedown.effectiveness.channel.cloudflare",
  host_abuse: "takedown.effectiveness.channel.host_abuse",
  registrar_abuse: "takedown.effectiveness.channel.registrar_abuse",
};

const pct = (num: number, den: number): number => (den > 0 ? Math.round((num / den) * 100) : 0);

/**
 * Efficacia per-canale — GET /admin/takedown/effectiveness (contract §5.1/§13).
 * Quali canali ottengono più rimozioni, per orientare priorità/tuning della policy G1/G2.
 */
export const EffectivenessPanel: React.FC = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<ChannelEffectiveness[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { channels: data } = await takedownApi.getEffectiveness();
        if (active) setChannels(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (active) setError(t("takedown.effectiveness.error_load", { error: e?.message || e }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const sorted = useMemo(
    () => [...channels].sort((a, b) => pct(b.rimossi, b.inviati) - pct(a.rimossi, a.inviati)),
    [channels]
  );

  return (
    <Paper sx={{ borderRadius: "10px", p: "20px", mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <InsightsRoundedIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          {t("takedown.effectiveness.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.effectiveness.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={36} color="secondary" />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("takedown.effectiveness.table.channel")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.total")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.sent")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.removed")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.deadletter")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.superseded")}</TableCell>
                <TableCell sx={{ width: 200 }}>{t("takedown.effectiveness.table.removal_pct")}</TableCell>
                <TableCell align="right">{t("takedown.effectiveness.table.avg_hours")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((c) => {
                const p = pct(c.rimossi, c.inviati);
                return (
                  <TableRow key={c.channel} hover>
                    <TableCell>{CHANNEL_LABEL_KEY[c.channel] ? t(CHANNEL_LABEL_KEY[c.channel]) : c.channel}</TableCell>
                    <TableCell align="right">{c.tot}</TableCell>
                    <TableCell align="right">{c.inviati}</TableCell>
                    <TableCell align="right">{c.rimossi}</TableCell>
                    <TableCell align="right">
                      {c.deadletter > 0 ? <Chip size="small" color="error" label={c.deadletter} /> : c.deadletter}
                    </TableCell>
                    <TableCell align="right">{c.superseded}</TableCell>
                    <TableCell>
                      <Tooltip title={t("takedown.effectiveness.table.tooltip_sent", { removed: c.rimossi, sent: c.inviati })}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={p}
                            sx={{ flex: 1, height: 8, borderRadius: 4 }}
                            color={p >= 50 ? "success" : p >= 20 ? "warning" : "error"}
                          />
                          <Typography variant="caption" sx={{ minWidth: 32, textAlign: "right" }}>
                            {p}%
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{c.ore_medie != null ? c.ore_medie.toFixed(1) : "—"}</TableCell>
                  </TableRow>
                );
              })}
              {!sorted.length && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                      {t("takedown.effectiveness.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default EffectivenessPanel;
